import React, { useCallback, useMemo } from "react";
import { TreeItem } from "@components_v2/tree-view";
import { ImageDriveOn, ImageFolderSmallOn } from "@mzc-cloudplex/icons";
import FolderTreeViewSkeleton from "./folder-tree-view-skeleton";
import useTreeItemDnd from "../hooks/use-tree-item-dnd";

const FolderTreeItem = ({
    folder,
    menuList,
    creatingParentFolder,
    foldDisabled,
    onClickFolder,
    onClickCreateFolder,
    onToggleFolderFold,
    onRefreshDrive,
    renderTree,
    onDropTreeItem,
    selectedFolderId,
    canEdit = true,
    folderMap,
    onDropFolderItem,
    draggingFolderIdRef,
    dragPreviewRef,
    movePendingFolderIds,
    activeDropRowRef,
    revealedFolderId,
    onRevealed,
}) => {
    const onHandleClickRefresh = useCallback(() => {
        onRefreshDrive(folder?.driveId, folder);
    }, [onRefreshDrive, folder]);

    const nodeId = useMemo(() => (folder?.id ? String(folder.id) : ""), [folder?.id]);

    const isRevealTarget = !!folder?.id && folder.id === revealedFolderId;
    // 이동 완료된 폴더 행으로 1회 스크롤 — 대상은 li가 아닌 행 div(하위 트리가 길면 li 기준 스크롤이 어긋남)
    const revealRef = useCallback(
        (node) => {
            if (!node || !isRevealTarget) return;
            node.querySelector(":scope > .MuiTreeItem-content")?.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
            });
            onRevealed?.(); // 실제 발화 시점을 부모에 알림 — 하이라이트 유지 타이머 시작 기준
        },
        [isRevealTarget, onRevealed],
    );

    const { dragProps, isIntoDropTarget, dropPosition, isDragSource } = useTreeItemDnd({
        folder,
        folderMap,
        foldDisabled,
        canEdit,
        selectedFolderId,
        onDropTreeItem,
        onDropFolderItem,
        onToggleFolderFold,
        draggingFolderIdRef,
        dragPreviewRef,
        movePendingFolderIds,
        activeDropRowRef,
    });

    if (!folder?.id) return null;

    return (
        <TreeItem
            key={folder.id}
            ref={isRevealTarget ? revealRef : undefined}
            className={isRevealTarget ? "revealHighlight" : undefined}
            type={"Folder"}
            depth={folder.depth}
            icon={
                folder.depth === 0 ? (
                    <ImageDriveOn height={16} width={16} />
                ) : (
                    <ImageFolderSmallOn height={16} width={16} />
                )
            }
            foldDisabled={foldDisabled}
            nodeId={nodeId}
            label={`${folder?.name}`}
            isOverCurrent={isIntoDropTarget}
            isDragSource={isDragSource}
            dropPosition={dropPosition === "into" ? undefined : dropPosition}
            {...dragProps}
            onClick={(e) => {
                e.stopPropagation();
                onClickFolder(folder, false, { toggleFold: true });
            }}
            onClickExpand={() => {
                // 설명: 열기/닫기 아이콘 전용
                onToggleFolderFold(folder);
            }}
            onClickRefresh={folder.type === "drive" && onRefreshDrive ? onHandleClickRefresh : undefined}
            menuList={menuList}
            sx={{
                ".MuiTypography-root": { fontSize: "14px", fontWeight: "normal" },
            }}
        >
            {creatingParentFolder?.id === folder.id && (
                <TreeItem
                    key={`inner_${folder.id}`}
                    depth={folder.depth + 1}
                    icon={<ImageFolderSmallOn height={16} width={16} />}
                    nodeId={"create"}
                    onSubmitField={(folderName) => {
                        onClickCreateFolder(folderName, folder.id);
                    }}
                ></TreeItem>
            )}
            {folder.children?.length > 0 && renderTree(folder?.id, folder?.children)}
            {folder.pending && <FolderTreeViewSkeleton type={"child"} depth={folder.depth + 1} />}
        </TreeItem>
    );
};

export default FolderTreeItem;
